import { useSortable } from "@dnd-kit/sortable";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Box } from "@mui/material";
import type { ReactNode } from "react";

interface ReorderableColumnHeaderProps {
	/** Column `field` — the dnd-kit sortable id; must be present in the enclosing SortableContext. */
	field: string;
	/** Accessible label for the drag handle. */
	reorderLabel: string;
	/** Optional testid for the drag handle. */
	testId?: string;
	/** The column's normal header content (sortable header, custom renderHeader output, or a label). */
	children: ReactNode;
}

/**
 * Wraps a column header with a hover-revealed drag handle for column reordering. Must render inside
 * a dnd-kit `DndContext` + horizontal `SortableContext` (SystemDataGrid provides these only when
 * `enableColumnReorder` is set).
 *
 * The drop-position indicator is NOT drawn here — it's painted on the full header *cell* by
 * SystemDataGrid during the drag, so it lands flush on the column boundary instead of inset within
 * this content wrapper. This component only owns the handle and the dragged-header dimming.
 */
export function ReorderableColumnHeader({ field, reorderLabel, testId, children }: ReorderableColumnHeaderProps) {
	const { setNodeRef, setActivatorNodeRef, listeners, attributes, isDragging } = useSortable({ id: field });

	return (
		<Box
			ref={setNodeRef}
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 0.25,
				width: "100%",
				opacity: isDragging ? 0.4 : 1,
				// Reveal the handle on header hover (and while dragging) only — keeps headers uncluttered at rest.
				"& .system-datagrid-drag-handle": { opacity: isDragging ? 1 : 0, transition: "opacity 120ms" },
				"&:hover .system-datagrid-drag-handle": { opacity: 1 },
			}}
		>
			<Box
				component="span"
				ref={setActivatorNodeRef}
				className="system-datagrid-drag-handle"
				aria-label={reorderLabel}
				data-testid={testId}
				{...attributes}
				{...listeners}
				// Stop a handle click from bubbling to a sortable header's sort-on-click handler.
				onClick={(e) => e.stopPropagation()}
				sx={{
					display: "flex",
					alignItems: "center",
					cursor: "grab",
					color: "text.disabled",
					touchAction: "none",
					"&:active": { cursor: "grabbing" },
					"&:hover": { color: "text.secondary" },
				}}
			>
				<DragIndicatorIcon sx={{ fontSize: 16 }} />
			</Box>
			<Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
		</Box>
	);
}
